import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { Bug, RefreshCw, Check, Copy, ExternalLink, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter89 — Diagnostics → Errors viewer. Super-admin only.
// Reads from /api/admin/diagnostics/errors. Grouped by default (same message+scope rolled
// into one row with count + last-seen). Expanding a row hits /occurrences for the individual
// rows. Filters: timeframe, reviewed, role, page, free-text. Actions: mark single, mark
// whole group, copy details. Auto-refreshes on the timeframe change.

const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const truncate = (s, n = 200) => (s && s.length > n ? s.slice(0, n) + '…' : (s || ''));

const RoleBadge = ({ role }) => {
  if (!role) return <span className="text-[#525252] text-xs">—</span>;
  const colors = {
    super_admin:  'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30',
    admin:        'bg-blue-500/15 text-blue-300 border-blue-500/30',
    service_manager: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
    office_staff: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    technician:   'bg-orange-500/15 text-orange-300 border-orange-500/30',
    employee:     'bg-orange-500/15 text-orange-300 border-orange-500/30',
    customer:     'bg-[#262626] text-[#A3A3A3] border-[#262626]',
    vip:          'bg-pink-500/15 text-pink-300 border-pink-500/30',
  };
  const c = colors[role] || 'bg-[#262626] text-[#A3A3A3] border-[#262626]';
  return <Badge className={`${c} text-[10px]`}>{role}</Badge>;
};

const DiagnosticsErrorsForm = () => {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [grouped, setGrouped] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('24h');
  const [reviewed, setReviewed] = useState('');     // '' | 'true' | 'false'
  const [roleFilter, setRoleFilter] = useState('');
  const [pageFilter, setPageFilter] = useState('');
  const [q, setQ] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);
  const [expandedOccurrences, setExpandedOccurrences] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { timeframe, group: grouped };
      if (reviewed) params.reviewed = reviewed;
      if (roleFilter) params.role = roleFilter;
      if (pageFilter) params.page = pageFilter;
      if (q) params.q = q;
      const { data } = await axios.get(`${API}/admin/diagnostics/errors`, { params, withCredentials: true });
      setRows(data?.rows || []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load errors');
    }
    setLoading(false);
  }, [timeframe, grouped, reviewed, roleFilter, pageFilter, q]);

  useEffect(() => { if (isSuper) load(); /* eslint-disable-next-line */ }, [timeframe, grouped, reviewed, roleFilter]);

  const onSearch = (e) => { e?.preventDefault(); load(); };

  const expandGroup = async (row) => {
    if (!grouped) return;
    const key = row.group_id;
    if (expandedKey === key) {
      setExpandedKey(null);
      setExpandedOccurrences([]);
      return;
    }
    setExpandedKey(key);
    setExpandedOccurrences([]);
    setExpandedLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/diagnostics/errors/${row.sample.id}/occurrences`, { withCredentials: true });
      setExpandedOccurrences(data?.occurrences || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load occurrences');
    }
    setExpandedLoading(false);
  };

  const markOne = async (errId, mark = true) => {
    try {
      await axios.post(`${API}/admin/diagnostics/errors/${errId}/review`, { reviewed: mark }, { withCredentials: true });
      toast.success(mark ? 'Marked reviewed' : 'Reopened');
      // Refresh both the list AND the expanded view
      load();
      if (expandedKey) {
        const fresh = await axios.get(`${API}/admin/diagnostics/errors/${errId}/occurrences`, { withCredentials: true }).then(r => r.data?.occurrences || []).catch(() => []);
        setExpandedOccurrences(fresh);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const markGroup = async (row) => {
    if (!window.confirm(`Mark all ${row.count} occurrence${row.count === 1 ? '' : 's'} of this error as reviewed?`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/diagnostics/errors/group-review`, {
        message: row.message,
        scope:   row.scope,
      }, { withCredentials: true });
      toast.success(`Marked ${data.modified || 0} reviewed`);
      load();
      if (expandedKey === row.group_id && row.sample?.id) {
        const fresh = await axios.get(`${API}/admin/diagnostics/errors/${row.sample.id}/occurrences`, { withCredentials: true }).then(r => r.data?.occurrences || []).catch(() => []);
        setExpandedOccurrences(fresh);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Group update failed');
    }
  };

  const copyDetails = async (row) => {
    const sample = row.sample || row;
    const text = [
      `NexFlow client error`,
      `Time:       ${sample.created_at || row.last_seen}`,
      `User:       ${sample.user_name || '—'} (${sample.user_role || '—'})`,
      `URL:        ${sample.url || '—'}`,
      `Scope:      ${row.scope || sample.scope || '—'}`,
      `App ver:    ${sample.app_version || '—'}`,
      `UA:         ${sample.ua || '—'}`,
      `Message:    ${row.message || sample.message || '—'}`,
      row.count ? `Occurrences: ${row.count}` : '',
      ``,
      `Stack:`,
      sample.stack || '—',
      ``,
      `Component stack:`,
      sample.component_stack || '—',
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied error details to clipboard');
    } catch {
      toast.error('Copy failed — clipboard blocked');
    }
  };

  const openPage = (row) => {
    const url = (row.sample || row).url;
    if (!url) { toast.error('No URL captured'); return; }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const clearFilters = () => {
    setTimeframe('24h');
    setReviewed('');
    setRoleFilter('');
    setPageFilter('');
    setQ('');
  };

  if (!isSuper) {
    return (
      <div className="bg-[#141414] border border-[#262626] rounded-md p-6 text-[#A3A3A3] text-sm">
        Diagnostics is restricted to Super Admin.
      </div>
    );
  }

  const hasAnyFilter = timeframe !== '24h' || reviewed || roleFilter || pageFilter || q;

  return (
    <div data-testid="diagnostics-errors-form" className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-white font-medium flex items-center gap-2"><Bug className="w-4 h-4 text-[#D4AF37]" /> Errors (Diagnostics)</h3>
          <p className="text-[#A3A3A3] text-sm mt-1">
            Client-side React render errors captured by the app-wide ErrorBoundary. Use this to
            triage crashes reported in the field. Errors are grouped by message + scope.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]" data-testid="diagnostics-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <form onSubmit={onSearch} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#525252]">Timeframe</Label>
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger data-testid="diag-filter-timeframe" className="bg-[#141414] border-[#262626] text-white h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              <SelectItem value="24h" className="text-white text-xs">Last 24 hours</SelectItem>
              <SelectItem value="7d" className="text-white text-xs">Last 7 days</SelectItem>
              <SelectItem value="30d" className="text-white text-xs">Last 30 days</SelectItem>
              <SelectItem value="all" className="text-white text-xs">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#525252]">Status</Label>
          <Select value={reviewed || '__all__'} onValueChange={(v) => setReviewed(v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid="diag-filter-status" className="bg-[#141414] border-[#262626] text-white h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              <SelectItem value="__all__" className="text-white text-xs">All</SelectItem>
              <SelectItem value="false" className="text-white text-xs">Unreviewed only</SelectItem>
              <SelectItem value="true" className="text-white text-xs">Reviewed only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#525252]">Role</Label>
          <Select value={roleFilter || '__all__'} onValueChange={(v) => setRoleFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid="diag-filter-role" className="bg-[#141414] border-[#262626] text-white h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              <SelectItem value="__all__" className="text-white text-xs">All roles</SelectItem>
              <SelectItem value="super_admin" className="text-white text-xs">Super Admin</SelectItem>
              <SelectItem value="admin" className="text-white text-xs">Admin</SelectItem>
              <SelectItem value="service_manager" className="text-white text-xs">Service Manager</SelectItem>
              <SelectItem value="construction_manager" className="text-white text-xs">Construction Manager</SelectItem>
              <SelectItem value="office_staff" className="text-white text-xs">Office Staff</SelectItem>
              <SelectItem value="technician" className="text-white text-xs">Technician</SelectItem>
              <SelectItem value="employee" className="text-white text-xs">Employee</SelectItem>
              <SelectItem value="customer" className="text-white text-xs">Customer</SelectItem>
              <SelectItem value="vip" className="text-white text-xs">VIP</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-wider text-[#525252]">Page contains</Label>
          <Input data-testid="diag-filter-page" value={pageFilter} onChange={(e) => setPageFilter(e.target.value)} placeholder="/admin/workforce" className="bg-[#141414] border-[#262626] text-white h-9 text-xs" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-wider text-[#525252]">Message/stack search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#525252]" />
            <Input data-testid="diag-filter-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="TypeError, undefined…" className="bg-[#141414] border-[#262626] text-white h-9 text-xs pl-7" />
          </div>
        </div>
        <Button type="submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-9 text-xs" data-testid="diag-apply-search">Search</Button>
        {hasAnyFilter && (
          <button type="button" data-testid="diag-clear-filters" onClick={clearFilters} className="text-[#D4AF37] text-xs hover:text-[#F3E5AB] underline">Clear filters</button>
        )}
        <label className="flex items-center gap-2 text-xs text-[#A3A3A3] ml-auto cursor-pointer">
          <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} data-testid="diag-grouped-toggle" />
          Group similar
        </label>
      </form>

      {/* Body */}
      {loading ? (
        <div className="text-[#A3A3A3] text-sm py-8 text-center" data-testid="diag-loading">Loading errors…</div>
      ) : error ? (
        <div className="bg-[#7F1D1D]/20 border border-[#7F1D1D] rounded-md p-4 text-red-300 text-sm" data-testid="diag-error">
          <p className="font-bold mb-1">Couldn&apos;t load errors</p>
          <p className="text-xs">{error}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3] text-sm" data-testid="diag-empty">
          {hasAnyFilter ? <>No errors match the current filters. <button onClick={clearFilters} className="text-[#D4AF37] underline hover:text-[#F3E5AB]">Clear filters</button></> : '🎉 No client errors in the selected window.'}
        </div>
      ) : (
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-[#A3A3A3] text-xs uppercase">
              <tr>
                <th className="w-6" />
                <th className="text-left px-3 py-2">Last seen</th>
                {grouped && <th className="text-left px-3 py-2">Count</th>}
                <th className="text-left px-3 py-2">Message</th>
                <th className="text-left px-3 py-2">User · Role</th>
                <th className="text-left px-3 py-2">Page</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const sample = row.sample || row;
                const key = grouped ? row.group_id : sample.id;
                const isOpen = expandedKey === key;
                const unreviewed = grouped ? row.any_unreviewed : !sample.reviewed;
                return (
                  <React.Fragment key={key}>
                    <tr
                      data-testid={`diag-row-${key}`}
                      className={`border-t border-[#262626] hover:bg-[#1a1a1a] ${grouped ? 'cursor-pointer' : ''}`}
                      onClick={() => grouped && expandGroup(row)}
                    >
                      <td className="px-2 py-3 text-[#525252]">
                        {grouped ? (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : null}
                      </td>
                      <td className="px-3 py-3 text-[#A3A3A3] text-xs whitespace-nowrap">
                        {fmtDT(grouped ? row.last_seen : sample.created_at)}
                        {unreviewed && <Badge className="ml-2 bg-red-500/15 text-red-300 border-red-500/30 text-[9px]">NEW</Badge>}
                      </td>
                      {grouped && <td className="px-3 py-3 text-[#D4AF37] font-bold text-xs">×{row.count}</td>}
                      <td className="px-3 py-3 text-white">
                        <div className="font-mono text-xs break-all">{truncate(row.message || sample.message, 140)}</div>
                        {(row.scope || sample.scope) && <div className="text-[10px] text-[#525252] mt-0.5">{row.scope || sample.scope}</div>}
                      </td>
                      <td className="px-3 py-3 text-xs">
                        <div className="text-white">{sample.user_name || <span className="text-[#525252]">anonymous</span>}</div>
                        <div className="mt-0.5"><RoleBadge role={sample.user_role} /></div>
                      </td>
                      <td className="px-3 py-3 text-[#A3A3A3] text-xs">
                        <div className="break-all">{truncate((sample.url || '').replace(/^https?:\/\/[^/]+/, ''), 64) || '—'}</div>
                        {sample.app_version && <div className="text-[10px] text-[#525252] mt-0.5">{sample.app_version}</div>}
                      </td>
                      <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex flex-wrap gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => copyDetails(row)} title="Copy details" className="border-[#262626] text-white hover:bg-[#262626] h-7 w-7 p-0" data-testid={`diag-copy-${key}`}><Copy className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" onClick={() => openPage(row)} title="Open affected page" className="border-[#262626] text-white hover:bg-[#262626] h-7 w-7 p-0" data-testid={`diag-open-${key}`}><ExternalLink className="w-3.5 h-3.5" /></Button>
                          {grouped ? (
                            row.any_unreviewed && (
                              <Button size="sm" onClick={() => markGroup(row)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-7 text-[10px]" data-testid={`diag-mark-group-${key}`}>
                                <Check className="w-3 h-3 mr-1" /> Mark group reviewed
                              </Button>
                            )
                          ) : (
                            <Button size="sm" onClick={() => markOne(sample.id, !sample.reviewed)} className={sample.reviewed ? 'bg-[#262626] text-[#A3A3A3] hover:bg-[#333] h-7 text-[10px]' : 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-7 text-[10px]'} data-testid={`diag-mark-one-${key}`}>
                              <Check className="w-3 h-3 mr-1" /> {sample.reviewed ? 'Reopen' : 'Mark reviewed'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {grouped && isOpen && (
                      <tr className="bg-[#0A0A0A] border-t border-[#262626]" data-testid={`diag-expanded-${key}`}>
                        <td colSpan={7} className="px-4 py-4">
                          {/* Sample stack trace from the group */}
                          <div className="bg-black/40 border border-[#262626] rounded-md p-3 mb-3">
                            <p className="text-[10px] uppercase text-[#525252] mb-1">Sample stack</p>
                            <pre className="text-xs text-[#A3A3A3] whitespace-pre-wrap break-all max-h-48 overflow-y-auto">{sample.stack || '— no stack captured —'}</pre>
                            {sample.component_stack && (
                              <>
                                <p className="text-[10px] uppercase text-[#525252] mt-2 mb-1">Component stack</p>
                                <pre className="text-xs text-[#525252] whitespace-pre-wrap break-all max-h-32 overflow-y-auto">{sample.component_stack}</pre>
                              </>
                            )}
                          </div>
                          {/* Occurrences */}
                          {expandedLoading ? (
                            <div className="text-xs text-[#A3A3A3] py-3 text-center">Loading occurrences…</div>
                          ) : (
                            <>
                              <p className="text-[10px] uppercase tracking-wider text-[#525252] mb-2">Occurrences ({expandedOccurrences.length})</p>
                              <div className="space-y-2">
                                {expandedOccurrences.map((o) => (
                                  <div key={o.id} className="bg-[#141414] border border-[#262626] rounded-md p-2.5 flex items-center justify-between gap-3 flex-wrap text-xs">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-white">{fmtDT(o.created_at)} · {o.user_name || <span className="text-[#525252]">anonymous</span>} <RoleBadge role={o.user_role} /></div>
                                      <div className="text-[#A3A3A3] text-[11px] break-all mt-0.5">{(o.url || '').replace(/^https?:\/\/[^/]+/, '')}</div>
                                      <div className="text-[#525252] text-[10px] mt-0.5">{o.ua}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {o.reviewed ? (
                                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
                                          Reviewed by {o.reviewed_by_name || '—'}
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-red-500/15 text-red-300 border-red-500/30 text-[10px]">New</Badge>
                                      )}
                                      <Button size="sm" variant="outline" onClick={() => markOne(o.id, !o.reviewed)} className="border-[#262626] text-white hover:bg-[#262626] h-6 text-[10px]" data-testid={`diag-mark-occ-${o.id}`}>
                                        {o.reviewed ? 'Reopen' : 'Mark reviewed'}
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DiagnosticsErrorsForm;
